import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/atoms';
import { useMovieActions } from '../../hooks/useMovies';
import { useCastActions } from '../../hooks/useCasts';
import { useCastAssignment } from '../../hooks/useCasts';
import type { MovieDetailsResponse } from '../../types/api/movie';
import { formatDateForDisplay, formatDateTimeForDisplay } from '../../types/api/movie';
import { resolveAssetUrl } from '../../utils/url';
import toast from 'react-hot-toast';
import { LoadingSpinner } from '../../components/atoms';
import { CastAssignmentModal } from '../../components/organisms/CastAssignmentModal';

/**
 * MovieDetailPage Component
 * 
 * Separate page for viewing movie details
 * Features:
 * - Display movie information
 * - Show genres and primary genre
 * - Display thumbnail
 * - Show movie metadata
 * - Navigation to edit page
 */
export const MovieDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { movieId } = useParams<{ movieId: string }>();
  const { getMovie } = useMovieActions();
  const { searchCasts } = useCastActions();
  const [isCastModalOpen, setIsCastModalOpen] = useState(false);

  const [movieDetails, setMovieDetails] = useState<MovieDetailsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const {
    assignCast,
    removeCast,
    updateRole,
    getCasts,
  } = useCastAssignment('movie', movieId || '');

  // Load movie data
  useEffect(() => {
    if (!movieId) {
      toast.error('Movie ID is required');
      navigate('/pages/movies');
      return;
    }

    const loadMovie = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const movieDetailsData = await getMovie(movieId);
        setMovieDetails(movieDetailsData);
        setError(null);
      } catch (error: any) {
        // Don't show error if request was cancelled (e.g., by request deduplication)
        // Axios cancelled errors have specific properties or messages
        const isCancelled = 
          error?.message?.includes('cancel') || 
          error?.message?.includes('superseded') ||
          error?.message?.includes('Cancel') ||
          error?.error?.code === 'CANCEL' ||
          (error?.name === 'Cancel' || error?.name === 'CanceledError');
        
        if (isCancelled) {
          // Request was cancelled, likely due to request deduplication
          // This is normal in React StrictMode, so we don't show an error
          // The other request should handle the response
          return;
        }
        
        console.error('Failed to load movie:', error);
        // Don't navigate immediately - allow retry mechanism to work
        // Check if it's a retryable error (network/server error)
        const isRetryableError = 
          error?.error?.type === 'NETWORK_ERROR' || 
          error?.error?.type === 'SERVER_ERROR' ||
          error?.error?.code?.startsWith('5') ||
          error?.error?.code?.startsWith('HTTP_5') ||
          error?.error?.code === 'TIMEOUT';
        
        if (isRetryableError) {
          // For retryable errors, show error but don't navigate
          // The HttpClient retry logic will handle retries
          setError(error.message || 'Failed to load movie. Please try again.');
         // toast.error(error.message || 'Failed to load movie. Retrying...');
        } else {
          // For non-retryable errors (404, 403, etc.), navigate away
          setError(error.message || 'Failed to load movie');
        //  toast.error(error.message || 'Failed to load movie');
          // Only navigate for non-retryable errors
          if (error?.error?.type === 'NOT_FOUND' || error?.error?.code === 'HTTP_404') {
            navigate('/pages/movies');
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadMovie();
  }, [movieId, navigate, getMovie]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!movieDetails || !movieDetails.movie) {
    return (
      <div className="text-center py-12">
        {error ? (
          <>
            <p className="text-red-600 mb-4">{error}</p>
            <div className="flex items-center justify-center gap-3">
              <Button
                variant="primary"
                onClick={() => {
                  setError(null);
                  setIsLoading(true);
                  getMovie(movieId!)
                    .then((data) => {
                      setMovieDetails(data);
                      setError(null);
                      toast.success('Movie loaded successfully');
                    })
                    .catch((err: any) => {
                      setError(err.message || 'Failed to load movie');
                      toast.error(err.message || 'Failed to load movie');
                    })
                    .finally(() => setIsLoading(false));
                }}
                className="mt-4"
              >
                Retry
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/pages/movies')}
                className="mt-4"
              >
                Back to Movies
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-gray-600">Movie not found</p>
            <Button
              variant="outline"
              onClick={() => navigate('/pages/movies')}
              className="mt-4"
            >
              Back to Movies
            </Button>
          </>
        )}
      </div>
    );
  }

  const movie = movieDetails.movie;

  const handleFetchAssignedCasts = async () => {
    return await getCasts();
  };

  const handleSearchCasts = async (keyword: string) => {
    const page = 1; // backend now 1-based
    const size = 10;
    const pageData = await searchCasts(keyword, page, size);
    return pageData.items;
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 text-left">Movie Details</h1>
          <p className="text-gray-600 mt-1">View comprehensive movie information</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            onClick={() => setIsCastModalOpen(true)}
          >
            Manage Casts
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/pages/movies')}
            leftIcon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            }
          >
            Back to Movies
          </Button>
          <Button
            variant="primary"
            onClick={() => navigate(`/pages/movies/${movie.contentId}/edit`)}
            leftIcon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            }
          >
            Edit Movie
          </Button>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Thumbnail Card */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-6">
            {/* Thumbnail */}
            <div className="relative mb-6">
              {movie.thumbnailUrl ? (
                <img
                  src={resolveAssetUrl(movie.thumbnailUrl)}
                  alt={movie.title}
                  className="w-full aspect-[2/3] rounded-xl border border-gray-300 object-cover shadow-md"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
              ) : null}
              <div 
                className="w-full aspect-[2/3] bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl border border-gray-300 flex items-center justify-center shadow-md" 
                style={{ display: movie.thumbnailUrl ? 'none' : 'flex' }}
              >
                <div className="text-center">
                  <svg className="w-20 h-20 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm font-medium text-gray-500">No Thumbnail</p>
                </div>
              </div>
            </div>

            {/* Status Badges */}
            <div className="space-y-3">
              <div className="text-center">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Status</p>
                <span
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold text-left ${
                    movie.status === 'ACTIVE'
                      ? 'bg-green-50 text-green-700 border border-green-200'
                      : movie.status === 'INACTIVE'
                      ? 'bg-gray-50 text-gray-700 border border-gray-200'
                      : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                  }`}
                >
                  {movie.status}
                </span>
              </div>
              <div className="text-center">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Access Level</p>
                <span
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold ${
                    movie.accessLevel === 'FREE'
                      ? 'bg-blue-50 text-blue-700 border border-blue-200'
                      : movie.accessLevel === 'PREMIUM'
                      ? 'bg-purple-50 text-purple-700 border border-purple-200'
                      : 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                  }`}
                >
                  {movie.accessLevel}
                </span>
              </div>
              <div className="text-center">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Video Status</p>
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold ${
                      movieDetails.hasReadyVideos
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-gray-50 text-gray-700 border border-gray-200'
                    }`}
                  >
                    {movieDetails.hasReadyVideos ? (
                      <>
                        <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Ready
                      </>
                    ) : (
                      'Not Ready'
                    )}
                  </span>
                  {movieDetails.videoVariants && movieDetails.videoVariants.length > 0 && (
                    <span className="text-xs text-gray-500">
                      {movieDetails.videoVariants.length} variant{movieDetails.videoVariants.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Movie Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Title Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-left">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">{movie.title}</h2>
            <p className="text-gray-600 leading-relaxed text-base">{movie.description}</p>
          </div>

          {/* Movie Details Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center pb-4">
              <svg className="w-5 h-5 mr-2 text-[#EAB308]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Movie Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-[#EAB308]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Director</p>
                  <p className="text-gray-900 font-medium">{movie.director}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Runtime</p>
                  <p className="text-gray-900 font-medium">{movie.runtime} minutes</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Release Date</p>
                  <p className="text-gray-900 font-medium">{formatDateForDisplay(movie.releaseDate)}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6l4 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Priority</p>
                  <p className="text-gray-900 font-medium">{movie.priority ?? '—'}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-yellow-50 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Language</p>
                  <p className="text-gray-900 font-medium">{movie.languageName}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Upload Date</p>
                  <p className="text-gray-900 font-medium">{formatDateTimeForDisplay(movie.uploadDate)}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Content ID</p>
                  <p className="text-gray-900 font-medium text-xs font-mono break-all">{movie.contentId}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Genres Card */}
          {movieDetails.genres && movieDetails.genres.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center pb-4">
                <svg className="w-5 h-5 mr-2 text-[#EAB308]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                Genres
              </h3>
              <div className="flex flex-wrap gap-3">
                {movieDetails.genres.map((genre) => (
                  <span
                    key={genre.genreId}
                    className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      genre.isPrimary
                        ? 'bg-[#EAB308] text-white shadow-sm'
                        : 'bg-gray-50 text-gray-800 border border-gray-200'
                    }`}
                    style={genre.isPrimary ? {} : { borderColor: genre.colorCode || '#E5E7EB', borderWidth: '2px' }}
                  >
                    {genre.name}
                    {genre.isPrimary && (
                      <span className="ml-2 text-xs bg-white/20 px-2 py-0.5 rounded-full">Primary</span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cast Assignment Modal */}
      {movieId && (
        <CastAssignmentModal
          isOpen={isCastModalOpen}
          onClose={() => setIsCastModalOpen(false)}
          contentType="movie"
          contentId={movieId}
          contentTitle={movie.title}
          fetchAssignedCasts={handleFetchAssignedCasts}
          searchCasts={handleSearchCasts}
          onAssign={async (castId, role) => {
            await assignCast(castId, role);
          }}
          onRemove={async (castId) => {
            await removeCast(castId);
          }}
          onUpdateRole={async (castId, role) => {
            await updateRole(castId, role);
          }}
        />
      )}
    </div>
  );
};

export default MovieDetailPage;

